// What the account stream SAYS HAPPENED, as opposed to the rows it carries.
//
// `account-stream-store.ts` holds rows for the views that render the stream's
// own projection (the overlay boards). Everything else in the SPA reads REST
// through TanStack Query, and for those the stream's job is narrower: tell them
// WHEN to re-read. This feed is that signal — one listener set per connection
// owner, fed once per network chunk by `accountStream.ts`, read by
// `live-query.ts`. It carries identities only, never a row: a consumer that
// hears a change re-READS (`apps/dashboard/CLAUDE.md` § Boundary), so nothing
// here can paint an echo.

/** One stream event a live read cares about. */
export type StreamSignal =
  /**
   * A connection's opening `ready`: from here its snapshot is the truth for
   * `subscribed`. Two facts say which reads it could have staled:
   * - `since` (epoch ms): when THIS connection's dial started. Its snapshot
   *   reflects state from then on, so a read made BEFORE it — during an outage
   *   included — re-reads once.
   * - `resumed`: the topics this tab held rows for from the previous connection.
   *   The server's diff state is per connection, so a row that vanished between
   *   the two (an approval decided mid-outage) arrives as NO patch at all, and no
   *   frame says when a snapshot is complete. Every read of a resumed topic
   *   re-reads, however fresh: the re-read is the only thing that can see the gap.
   * - `continued`: the topics carried over WITHOUT a gap — the previous
   *   connection served them live right up to OUR abort of it, and the same
   *   re-run dialled this one at once (a route change altering the topic set).
   *   Never across an idle dwell with no topics, a drop, a recycle, an account
   *   switch, or a first connection: empty then. Neither rule above applies to
   *   them: that connection vouched for their reads until the instant it closed.
   *   Only the dial itself is unwatched; a row that moved in it arrives as news in
   *   the new snapshot, and one that vanished in that one round trip waits for
   *   the next change (a live read does not poll).
   */
  | {
      readonly kind: 'ready';
      readonly subscribed: readonly string[];
      readonly since: number;
      readonly resumed: readonly string[];
      readonly continued: readonly string[];
    }
  /** One row under `(topic, key)` was upserted or removed. `key` is the entity id. */
  | { readonly kind: 'changed'; readonly topic: string; readonly key: string };

export type ChangeFeed = {
  /** Hear every chunk's signals, in arrival order. Returns the unsubscribe. */
  readonly listen: (listener: (signals: readonly StreamSignal[]) => void) => () => void;
};

export type ChangeFeedSource = ChangeFeed & {
  /** Hand one chunk's signals to every listener. An empty chunk is not sent. */
  readonly emit: (signals: readonly StreamSignal[]) => void;
};

/**
 * One feed. Lives exactly as long as the stream that owns it (the shell hub, or
 * a route's own connection), so its listener set cannot outlive a mount: each
 * listener is removed by the cleanup of the scope that added it.
 */
export function createChangeFeed(): ChangeFeedSource {
  const listeners = new Set<(signals: readonly StreamSignal[]) => void>();
  return {
    listen: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    emit: (signals) => {
      if (signals.length === 0) return;
      for (const listener of [...listeners]) listener(signals);
    },
  };
}
